import { useEffect, useState, useCallback, useContext } from 'react'
import { Link, useParams } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { colors } from '../../styles/tokens'
import StatusBadge from '../../components/StatusBadge'
import { Button } from '../../components/FormControls'
import { EmptyState, ErrorBanner, SkeletonRows } from '../../components/Feedback'
import EstimationBreadcrumb from '../../components/EstimationBreadcrumb'
import { ProjectContext } from '../../context/ProjectContext'

// Suggestions only -- the currency input below accepts ANY typed value
// (any ISO 4217 code), not just these. Ordered: GCC/regional first
// (most relevant to this app's actual usage), then other common
// currencies.
const CURRENCY_SUGGESTIONS = [
  'SAR', 'AED', 'EGP', 'KWD', 'QAR', 'BHD', 'OMR', 'JOD',
  'USD', 'EUR', 'GBP', 'CHF', 'JPY', 'CNY', 'INR', 'TRY',
]

export default function TenderWorkspacePage() {
  const { tenderId } = useParams()
  const selectedProject = useContext(ProjectContext)
  const [tender, setTender] = useState(null)
  const [revisions, setRevisions] = useState(null)
  const [error, setError] = useState(null)
  const [creating, setCreating] = useState(false)
  // Phase B: read-only mapping state for this Tender, plus the smallest
  // UI to associate it with the currently selected CostSpace Project.
  // undefined = not loaded yet, null = confirmed unmapped, object = mapped.
  const [mappedProject, setMappedProject] = useState(undefined)
  const [mapping, setMapping] = useState(false)
  const [mapError, setMapError] = useState(null)

  // Tender Currency. tender.currency is the single authoritative source
  // (estimation.tender.currency) for every tender-level financial
  // display across Estimation -- BOQ, Rate Build-Up, Budget Summary,
  // Indirect/Risk/HO, Markup, Selling Price. Editability is gated by the
  // SAME rule the database itself enforces
  // (enforce_tender_currency_immutability trigger): changing currency is
  // blocked once any rate_buildup_line exists for this tender, or once
  // any of its revisions is frozen. This UI check is advisory (the DB
  // trigger is the real enforcement); it exists only to give the user a
  // clear reason up front instead of a raw constraint-violation error
  // after submitting.
  const [editingCurrency, setEditingCurrency] = useState(false)
  const [currencyDraft, setCurrencyDraft] = useState('')
  const [currencyLocked, setCurrencyLocked] = useState(null) // null = not checked yet, { hasPricing, hasFrozen } once checked
  const [currencySaving, setCurrencySaving] = useState(false)
  const [currencyError, setCurrencyError] = useState(null)

  const checkCurrencyLock = useCallback(async () => {
    // Two-step lookup rather than a nested PostgREST filter across two
    // foreign tables (boq_item -> estimate_revision -> tender_id) -- the
    // same JOIN-in-JS pattern already used in RateBuildUpPage and
    // DirectBudgetReviewPage for relations with no clean embeddable FK
    // path, kept here for the same reliability reason.
    const { data: revRows } = await supabase.from('estimate_revision').select('id, status').eq('tender_id', tenderId)
    const revIds = (revRows || []).map((r) => r.id)
    const hasFrozen = (revRows || []).some((r) => r.status === 'frozen')
    let hasPricing = false
    if (revIds.length) {
      const { data: boqRows } = await supabase.from('boq_item').select('id').in('estimate_revision_id', revIds)
      const boqIds = (boqRows || []).map((b) => b.id)
      if (boqIds.length) {
        const { count: lineCount } = await supabase.from('rate_buildup_line')
          .select('id', { count: 'exact', head: true }).in('boq_item_id', boqIds)
        hasPricing = (lineCount ?? 0) > 0
      }
    }
    setCurrencyLocked({ hasPricing, hasFrozen })
  }, [tenderId])

  useEffect(() => { checkCurrencyLock() }, [checkCurrencyLock])

  async function saveCurrency() {
    if (!currencyDraft) { setCurrencyError('Select a currency.'); return }
    setCurrencySaving(true)
    setCurrencyError(null)
    const { error: saveErr } = await supabase.from('tender').update({ currency: currencyDraft }).eq('id', tenderId)
    setCurrencySaving(false)
    if (saveErr) {
      // The DB trigger is the real guard; surface its message verbatim
      // rather than inventing a different one -- it already explains
      // exactly which condition (pricing exists / a revision is frozen)
      // blocked the change.
      setCurrencyError(saveErr.message)
      return
    }
    setEditingCurrency(false)
    await loadData()
  }

  const loadData = useCallback(async () => {
    const [tenderRes, revisionsRes] = await Promise.all([
      supabase.from('tender').select('*').eq('id', tenderId).single(),
      supabase
        .from('estimate_revision')
        .select('id, revision_number, label, status, wbs_snapshot_source, created_at, updated_at')
        .eq('tender_id', tenderId)
        .order('revision_number', { ascending: false }),
    ])

    if (tenderRes.error) setError(tenderRes.error.message)
    else setTender(tenderRes.data)

    if (revisionsRes.error) setError(revisionsRes.error.message)
    else setRevisions(revisionsRes.data)
  }, [tenderId])

  // Read-only: this Tender's current Project mapping, if any. Never
  // writes anything -- viewing the workspace does not create a mapping.
  //
  // Two separate queries, not a nested select: the mapping row lives in
  // the `estimation` schema (this client's default), but the Project
  // row it points to lives in `public` -- PostgREST cannot resolve a
  // `projects(...)` embed across two different schemas from a client
  // pinned to `estimation`, so the previous single nested-select query
  // failed with "Could not find a relationship" regardless of schema
  // cache state. `.schema('public')` on the second call targets the
  // correct schema explicitly for that one lookup only; the client's
  // own default schema (estimation) is unchanged for every other query
  // in this file.
  const loadMapping = useCallback(async () => {
    const { data: mapRow, error: mapErr } = await supabase
      .from('tender_costspace_project_mapping')
      .select('costspace_project_id')
      .eq('tender_id', tenderId)
      .maybeSingle()
    if (mapErr) { setMapError(mapErr.message); return }

    if (!mapRow?.costspace_project_id) {
      setMappedProject(null)
      return
    }

    const { data: project, error: projErr } = await supabase
      .schema('public')
      .from('projects')
      .select('id, name, code')
      .eq('id', mapRow.costspace_project_id)
      .maybeSingle()
    if (projErr) { setMapError(projErr.message); return }
    setMappedProject(project ?? null)
  }, [tenderId])

  useEffect(() => {
    loadData()
    loadMapping()
  }, [loadData, loadMapping])

  async function handleCreateRevision() {
    setCreating(true)
    setError(null)

    const hasExisting = revisions && revisions.length > 0
    const { data: authData } = await supabase.auth.getUser()

    const { error } = await supabase.rpc('create_estimate_revision', {
      p_tender_id: tenderId,
      p_label: `Revision ${(revisions?.length ?? 0) + 1}`,
      p_wbs_source: hasExisting ? 'previous_revision' : 'master_wbs',
      p_source_revision_id: hasExisting ? revisions[0].id : null,
      p_created_by: authData.user?.id ?? null,
      p_reason_for_revision: hasExisting ? 'New revision from UI' : 'Initial revision from UI',
    })

    if (error) setError(error.message)
    else await loadData()

    setCreating(false)
  }

  // Phase B mapping UI: the ONLY write path is the approved
  // SECURITY DEFINER RPC (estimation.set_tender_project_mapping), which
  // itself enforces both est_has_tender_access AND project_members
  // authorization atomically. No direct INSERT/UPDATE against the
  // mapping table from this component.
  async function handleMapToSelectedProject() {
    if (!selectedProject?.id) return
    setMapping(true)
    setMapError(null)
    const { error } = await supabase.rpc('set_tender_project_mapping', {
      p_tender_id: tenderId,
      p_costspace_project_id: selectedProject.id,
    })
    if (error) setMapError(error.message)
    else await loadMapping()
    setMapping(false)
  }

  // The ONLY write path for removing an association is the approved
  // SECURITY DEFINER RPC (estimation.remove_tender_project_mapping),
  // which itself re-derives the actual linked Project from the mapping
  // row and re-checks both authorization systems -- never a direct
  // DELETE against the mapping table from this component. A plain
  // window.confirm is used for the explicit confirmation step, matching
  // the existing confirmation pattern already used elsewhere in
  // CostSpace (e.g. Projects.jsx's own delete action) rather than
  // introducing a new modal/design system.
  async function handleUnlink() {
    if (!mappedProject) return
    if (!window.confirm(`Unlink this Tender from "${mappedProject.name}"?`)) return
    setMapping(true)
    setMapError(null)
    const { error } = await supabase.rpc('remove_tender_project_mapping', { p_tender_id: tenderId })
    if (error) setMapError(error.message)
    else await loadMapping()
    setMapping(false)
  }

  if (error && !tender) return <ErrorBanner message={error} />
  if (!tender) return <SkeletonRows rows={2} cols={3} />

  const latest = revisions?.[0]
  const isMappedToSelected = mappedProject && selectedProject && mappedProject.id === selectedProject.id
  const isMappedToOther = mappedProject && (!selectedProject || mappedProject.id !== selectedProject.id)

  return (
    <div>
      <EstimationBreadcrumb />
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: colors.textPrimary, margin: 0 }}>{tender.name}</h1>
        <p style={{ fontSize: 13, color: colors.textSecondary, margin: '4px 0 0' }}>
          {tender.client_name} — {tender.location}
        </p>
      </div>

      {/* Tender Currency -- see state block above for the authoritative
          source and the lifecycle rule this mirrors. */}
      <div style={{ padding: '10px 16px', background: '#F1F5F9', borderRadius: 8, marginBottom: 16, fontSize: 13, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 600 }}>Currency:</span>
        {editingCurrency ? (
          <>
            {/* Free-text input with a suggestion list (not a locked
                <select>) -- any ISO currency code can be typed, not just
                the common ones offered below. The datalist is purely
                suggestions; nothing here validates or restricts what the
                user types. Uppercased on blur for consistency with how
                codes are stored/compared everywhere else (tender.currency,
                resource_rate.currency, exchange_rate.from/to_currency are
                all stored uppercase). */}
            <input
              list="currency-suggestions"
              value={currencyDraft}
              onChange={(e) => setCurrencyDraft(e.target.value)}
              onBlur={(e) => setCurrencyDraft(e.target.value.toUpperCase())}
              placeholder="e.g. SAR"
              style={{ padding: '4px 8px', borderRadius: 6, border: `1px solid ${colors.border}`, fontSize: 13, width: 100 }}
            />
            <datalist id="currency-suggestions">
              {CURRENCY_SUGGESTIONS.map((c) => <option key={c} value={c} />)}
            </datalist>
            <Button onClick={saveCurrency} disabled={currencySaving}>{currencySaving ? 'Saving…' : 'Save'}</Button>
            <Button onClick={() => { setEditingCurrency(false); setCurrencyError(null) }} variant="secondary">Cancel</Button>
          </>
        ) : (
          <>
            <strong>{tender.currency || 'Not set'}</strong>
            {currencyLocked && !currencyLocked.hasPricing && !currencyLocked.hasFrozen ? (
              <Button onClick={() => { setCurrencyDraft(tender.currency || ''); setEditingCurrency(true) }} variant="secondary">
                {tender.currency ? 'Change' : 'Set Currency'}
              </Button>
            ) : currencyLocked ? (
              <span style={{ color: colors.textMuted, fontSize: 12 }}>
                🔒 {currencyLocked.hasFrozen
                  ? 'Locked — a Revision has been frozen.'
                  : 'Locked — pricing already exists for this tender.'}
              </span>
            ) : null}
          </>
        )}
      </div>
      {currencyError && <ErrorBanner message={currencyError} />}

      {/* Phase B: CostSpace Project association -- read-only display of
          the existing mapping, plus (only when unmapped and a Project is
          currently selected in CostSpace) the single explicit action to
          create one via the approved RPC. Never a second Project
          selector -- selectedProject remains CostSpace's own state. */}
      {mappedProject !== undefined && (
        <div style={{ padding: '10px 16px', background: '#F1F5F9', borderRadius: 8, marginBottom: 16, fontSize: 13, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          {mappedProject ? (
            <span>
              🔗 CostSpace Project: <strong>{mappedProject.name}</strong>{mappedProject.code ? ` (${mappedProject.code})` : ''}
              {isMappedToOther && selectedProject && (
                <span style={{ color: colors.textMuted }}> — different from the currently selected Project ({selectedProject.name})</span>
              )}
            </span>
          ) : (
            <span style={{ color: colors.textMuted }}>Not associated with a CostSpace Project.</span>
          )}
          {!mappedProject && selectedProject?.id && (
            <Button onClick={handleMapToSelectedProject} disabled={mapping}>
              {mapping ? 'Linking…' : `Link to ${selectedProject.name}`}
            </Button>
          )}
          {mappedProject && (
            <Button onClick={handleUnlink} disabled={mapping}>
              {mapping ? 'Unlinking…' : 'Unlink'}
            </Button>
          )}
        </div>
      )}
      <ErrorBanner message={mapError} />

      {/* Summary strip — quick at-a-glance workspace status */}
      <div
        style={{
          display: 'flex',
          gap: 24,
          padding: '16px 20px',
          background: colors.surface,
          border: `1px solid ${colors.border}`,
          borderRadius: 8,
          marginBottom: 24,
        }}
      >
        <SummaryItem label="Total Revisions" value={revisions?.length ?? '—'} />
        <SummaryItem
          label="Current Status"
          value={latest ? <StatusBadge status={latest.status} /> : '—'}
        />
        <SummaryItem label="Tender Status" value={<StatusBadge status={tender.status} />} />
      </div>

      <ErrorBanner message={error} />

      {/* Tender-scoped Dictionary entry points -- only reachable from
          inside this Tender, so tenderId is always present. There is no
          global/company-wide route to either Dictionary anymore. */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        <Link
          to={`/estimation/tender/${tenderId}/resources`}
          style={{ fontSize: 13, fontWeight: 600, color: colors.primary, textDecoration: 'none', padding: '7px 14px', border: `1px solid ${colors.primary}`, borderRadius: 6 }}
        >
          Resource Dictionary
        </Link>
        <Link
          to={`/estimation/tender/${tenderId}/cost-codes`}
          style={{ fontSize: 13, fontWeight: 600, color: colors.primary, textDecoration: 'none', padding: '7px 14px', border: `1px solid ${colors.primary}`, borderRadius: 6 }}
        >
          Cost Code Dictionary
        </Link>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h2 style={{ fontSize: 15, fontWeight: 700, color: colors.textPrimary, margin: 0 }}>Revisions</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          {revisions?.length >= 2 && (
            <Link
              to={`/estimation/tender/${tenderId}/compare`}
              style={{ fontSize: 13, fontWeight: 600, color: colors.primary, textDecoration: 'none', padding: '7px 14px', border: `1px solid ${colors.primary}`, borderRadius: 6 }}
            >
              Compare Revisions →
            </Link>
          )}
          <Button onClick={handleCreateRevision} disabled={creating}>
            {creating ? 'Creating…' : '+ New Revision'}
          </Button>
        </div>
      </div>

      <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8, overflow: 'hidden' }}>
        {revisions?.length === 0 ? (
          <div style={{ padding: 24 }}>
            <EmptyState
              title="No revisions yet"
              description="Create the first revision to start building the estimate."
            />
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: '#FAFBFC', borderBottom: `1px solid ${colors.border}` }}>
                <Th>#</Th>
                <Th>Label</Th>
                <Th>Status</Th>
                <Th>WBS Source</Th>
                <Th align="right">Created</Th>
              </tr>
            </thead>
            <tbody>
              {revisions?.map((r) => (
                <tr key={r.id} style={{ borderBottom: `1px solid ${colors.border}` }}>
                  <Td muted>{r.revision_number}</Td>
                  <Td>
                    <Link
                      to={`/estimation/tender/${tenderId}/revision/${r.id}`}
                      style={{ color: colors.primary, fontWeight: 600, textDecoration: 'none' }}
                    >
                      {r.label}
                    </Link>
                  </Td>
                  <Td>
                    <StatusBadge status={r.status} />
                  </Td>
                  <Td muted>{r.wbs_snapshot_source}</Td>
                  <Td align="right" muted>
                    {new Date(r.created_at).toLocaleDateString()}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

function SummaryItem({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontSize: 15, fontWeight: 600, color: colors.textPrimary }}>{value}</div>
    </div>
  )
}

function Th({ children, align = 'left' }) {
  return (
    <th
      style={{
        textAlign: align,
        padding: '10px 20px',
        fontSize: 12,
        fontWeight: 600,
        color: colors.textSecondary,
        textTransform: 'uppercase',
        letterSpacing: 0.4,
      }}
    >
      {children}
    </th>
  )
}

function Td({ children, align = 'left', muted }) {
  return (
    <td
      style={{
        textAlign: align,
        padding: '12px 20px',
        color: muted ? colors.textMuted : colors.textPrimary,
        fontSize: muted ? 13 : 14,
      }}
    >
      {children}
    </td>
  )
}
